
import { Vue, Component, Prop } from 'vue-property-decorator';
import { AddressBookItem } from '@/features/shipment/types';
import { deleteAddressBookItem } from '@/features/shipment/services/addresses-book.services';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { showErrorAlertDialog, showSuccessAlertDialog, truncateText } from '@/features/common/helpers';

function flagSlugFromCountryCode(code: string): string | null {
  if (!code || typeof code !== 'string') return null;
  const cleaned = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cleaned)) return null;
  const aliases: Record<string, string> = {
    UK: 'gb', EN: 'gb', SF: 'gb', WL: 'gb', NB: 'gb',
  };
  return (aliases[cleaned] || cleaned).toLowerCase();
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function countryDisplayHtml(code: string, label: string): string {
  const slug = flagSlugFromCountryCode(code);
  const safeLabel = escapeHtml(label);
  if (!slug) return safeLabel;
  return `<span class="fi fi-${slug} address-item-flag" aria-hidden="true"></span>\u00A0${safeLabel}`;
}

@Component
export default class AddressItem extends Vue {
  @Prop({ required: true }) item!: AddressBookItem;

  private isDeleting = false;

  get addressString(): string {
    const raw = (this.item.countryCode || '').trim();
    const upper = raw.length >= 2 ? raw.slice(0, 2).toUpperCase() : '';
    const countryInfo = upper ? this.$store.getters.getCountryFromCode(upper) : null;
    const label = countryInfo?.country || raw || upper;
    const country = countryDisplayHtml(upper, label);

    const output: Array<string> = [
      this.item.company,
      this.item.contact,
      this.item.phone ? `<span data-clarity-mask="True">${this.item.phone}</span>` : '',
      this.item.emails && this.item.emails[0] ? `<span data-clarity-mask="True">${this.item.emails[0]}</span>` : '',
      this.item.addr1,
      this.item.city,
      this.item.province as string,
      this.item.postalCode,
      country,
      this.item.residential ? 'Residential Address' : '',
      this.item.taxId ? `Tax id: ${this.item.taxId}` : '',
    ];

    return output.filter(Boolean).join(', ');
  }

  private truncateText = truncateText;

  private editAddressBookItem() {
    this.$emit('edit', this.item);
  }

  private deleteAddressBookItem() {
    eventBus.$once(eventNames.CONFIRMATION_DIALOG, async () => {
      this.isDeleting = true;
      const isSuccess = await deleteAddressBookItem(this.item.id);

      if (isSuccess) {
        this.$emit('delete');
        showSuccessAlertDialog(this.$t('alertDialog.addressBookDeleted') as string);
      } else {
        showErrorAlertDialog(this.$t('alertDialog.somethingWentWrong') as string);
      }

      this.isDeleting = false;
    });

    this.$store.commit('toggleConfirmDialog', {
      show: true,
    });
  }
}
